import {
  ActivatedRouteSnapshot,
  Routes,
  RouterStateSnapshot,
} from '@angular/router';
import {
  AuthGuard,
  redirectLoggedInTo,
  redirectUnauthorizedTo,
} from '@angular/fire/auth-guard';
import { LoginComponent } from './pages/login/login.component';
import { UsersMapComponent } from './pages/users-map/users-map.component';
import { SpaceClaimComponent } from './pages/space-claim/space-claim.component';
import { ClaimFunnelComponent } from './pages/claim-funnel/claim-funnel.component';
import { AdminHomeComponent } from './pages/admin-home/admin-home.component';
import { AbuseComponent } from './pages/abuse/abuse.component';
import { EngagementComponent } from './pages/engagement/engagement.component';
import { MembersComponent } from './pages/members/members.component';
import { ProfileFunnelComponent } from './pages/profile-funnel/profile-funnel.component';
import { AbuseCaseComponent } from './pages/abuse/abuse-case/abuse-case.component';
import { EventsComponent } from './pages/events/events.component';
import { EventDetailComponent } from './pages/events/event-detail/event-detail.component';
import { AdminSectionComponent } from './components/admin-section/admin-section.component';
import { OutreachComponent } from './pages/outreach/outreach.component';
import { OutreachConversationComponent } from './pages/outreach/conversation-detail/outreach-conversation.component';

/*
 * Send an unauthenticated visitor to the login page, carrying where they were
 * headed. The spam-guard alerts link straight to /admin/abuse/<uid>, and
 * without this the 3am click lands on the users list having silently thrown
 * away the case it was pointing at.
 */
const redirectUnauthorizedToLogin = (
  _next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => redirectUnauthorizedTo(['admin', 'login', { redirect: state.url }]);
const redirectLoggedInToHome = () => redirectLoggedInTo(['admin']);

const guarded = {
  canActivate: [AuthGuard],
  data: { authGuardPipe: redirectUnauthorizedToLogin },
};

/*
 * The panel is three sections - People, Spaces, Abuse - each a shell with
 * tabs. A child route with `data.tab` is a tab; the shell reads them off this
 * config, so a new tab is one entry here and nothing else.
 *
 * The old flat URLs (/admin/members, /admin/engagement, /admin/events/<id> …)
 * redirect to their new homes rather than 404: they are in alert emails,
 * bookmarks and the app's own deep links.
 */
export const routes: Routes = [
  // "/" is served by the static marketing site (dist/index.html); the Angular
  // app is only reached via the app.html rewrite. Legacy deep links from
  // before the admin moved under /admin:
  { path: '', redirectTo: '/admin/login', pathMatch: 'full' },
  { path: 'login', redirectTo: '/admin/login', pathMatch: 'full' },
  { path: 'users', redirectTo: '/admin/people/members', pathMatch: 'full' },
  {
    path: 'admin',
    children: [
      {
        /* The front door. Unauthenticated visitors are sent to login by the
           guard, which now carries them back here afterwards. */
        path: '',
        component: AdminHomeComponent,
        pathMatch: 'full',
        ...guarded,
      },
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [AuthGuard],
        data: { authGuardPipe: redirectLoggedInToHome },
      },

      /* People: every account, from four sides. */
      {
        path: 'people',
        component: AdminSectionComponent,
        ...guarded,
        children: [
          { path: '', redirectTo: 'members', pathMatch: 'full' },
          { path: 'members', component: MembersComponent, data: { tab: 'Members' } },
          {
            /* The chat funnel, peer chats only; lobby rooms are under Spaces. */
            path: 'chats',
            component: EngagementComponent,
            data: { tab: 'Chats', kind: 'peer' },
          },
          { path: 'map', component: UsersMapComponent, data: { tab: 'Map' } },
          {
            path: 'profiles',
            component: ProfileFunnelComponent,
            data: { tab: 'Profile completion' },
          },
          /* Sharon and the copilot. See docs/plans/outreach-autodrive.md. */
          { path: 'outreach', component: OutreachComponent, data: { tab: 'Outreach' } },
        ],
      },
      {
        /* One Sharon conversation. The freeze and draft alert emails link
           here. Outside the section, like spaces/:id, so it is a page of its
           own and never a tab. */
        path: 'people/outreach/conversations/:id',
        component: OutreachConversationComponent,
        ...guarded,
      },

      /* Spaces: every event, its lobby, and whether its organizer claimed it. */
      {
        path: 'spaces',
        component: AdminSectionComponent,
        ...guarded,
        children: [
          { path: '', redirectTo: 'events', pathMatch: 'full' },
          { path: 'events', component: EventsComponent, data: { tab: 'Events' } },
          {
            path: 'lobbies',
            component: EngagementComponent,
            data: { tab: 'Lobbies', kind: 'lobby' },
          },
          { path: 'claims', component: ClaimFunnelComponent, data: { tab: 'Claim funnel' } },
        ],
      },
      {
        /* One space in full. After the section so /admin/spaces/events is the
           tab, and anything else after /spaces/ is an id. */
        path: 'spaces/:id',
        component: EventDetailComponent,
        ...guarded,
      },

      /* Abuse: the board, and one case. The spam-guard alert emails link
         straight to /admin/abuse/<uid>. */
      { path: 'abuse/:uid', component: AbuseCaseComponent, ...guarded },
      { path: 'abuse', component: AbuseComponent, ...guarded },

      /* Where each of the old pages went. */
      { path: 'users/list', redirectTo: 'people/members' },
      { path: 'users/map', redirectTo: 'people/map' },
      { path: 'users', redirectTo: 'people/members' },
      { path: 'members', redirectTo: 'people/members' },
      { path: 'engagement', redirectTo: 'people/chats' },
      { path: 'profile-funnel', redirectTo: 'people/profiles' },
      { path: 'events/:id', redirectTo: 'spaces/:id' },
      { path: 'events', redirectTo: 'spaces/events' },
      { path: 'claim-funnel', redirectTo: 'spaces/claims' },
    ],
  },
  // Public claim wizard — linked from campaign emails as
  // https://nx2u.app/spaceClaim?eventID=… ; must stay at this path.
  { path: 'spaceClaim', component: SpaceClaimComponent },
];
